import type { ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import { isWeb, OutlinedButton, Subheadline5, Text, YStack } from "@medbillai/ui";
import { flaggedBillsUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../../lib/analytics";
import { usePushGetHelp } from "../../../lib/usePushGetHelp";
import { WidgetWrapper } from "./WidgetWrapper";
type ClaimsReviewWidgetProps = {
  count: number;
  error: ApolloError | undefined;
};
export function ClaimsReviewWidget({
  count,
  error
}: ClaimsReviewWidgetProps) {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const {
    push
  } = useRouter();
  const handleReviewClick = useCallback(() => {
    analyticsService.trackEvent("ClaimsReviewTapped", {
      action: count > 0 ? "REVIEW_NOW" : "UPLOAD_BILL"
    });
    if (count > 0) {
      push(flaggedBillsUrl());
    } else {
      pushGetHelp({
        caseType: "BILL_ISSUE_V2",
        title: "Review my bill for errors",
        source: "home_claims_review_widget_upload"
      });
    }
  }, [count, push, pushGetHelp]);

  // Don't show widget if there's an error
  if (error) {
    return null;
  }
  return <WidgetWrapper title="Potential cases" showGradient={isWeb ? "inner" : "full"} data-sentry-element="WidgetWrapper" data-sentry-component="ClaimsReviewWidget" data-sentry-source-file="ClaimsReviewWidget.tsx">
      <YStack backgroundColor="$white1" borderRadius="$4" padding="$5" $platform-web={{
      maxWidth: 380
    }} width="100%" gap="$4" bordered alignSelf="center" data-sentry-element="YStack" data-sentry-source-file="ClaimsReviewWidget.tsx">
        <YStack gap="$2" data-sentry-element="YStack" data-sentry-source-file="ClaimsReviewWidget.tsx">
          {count > 0 ? <Subheadline5 color="$textDefault">
              We found{" "}
              <Text {...isWeb ? {
            backgroundColor: "$limegreen",
            paddingHorizontal: "$2",
            paddingVertical: "$1.5",
            borderRadius: "$8"
          } :
          // Ideally the above would "just work", but the native mobile
          // Text doesn't get any of the padding or border radius, just
          // the background. (ENG-5217)
          {
            bold: true
          }}>
                {count} {count === 1 ? "claim" : "claims"}
              </Text>{" "}
              with potential savings 💸 🔍
            </Subheadline5> : <Subheadline5 color="$textDefault">
              Received a bill you have questions about? Let us review it.
            </Subheadline5>}
        </YStack>

        <OutlinedButton onPress={handleReviewClick} data-sentry-element="OutlinedButton" data-sentry-source-file="ClaimsReviewWidget.tsx">
          {count > 0 ? "Review now" : "Get help with a bill"}
        </OutlinedButton>
      </YStack>
    </WidgetWrapper>;
}